import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <>
        <SEOHead title="文章未找到 - 白洁传研究中心" />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-24 pb-16 px-4 md:px-6">
            <div className="max-w-7xl mx-auto text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
                <i className="ri-file-search-line text-2xl"></i>
              </div>
              <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">文章未找到</h1>
              <p className="mt-3 text-foreground-600">该文章可能已被移除或链接有误</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
              >
                <i className="ri-home-line"></i>
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  if (relatedArticles.length === 0) {
    const otherArticles = articles.filter((a) => a.id !== article.id).slice(0, 3);
    relatedArticles.push(...otherArticles);
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.date,
    url: `https://baijiezhuan.com/article/${article.id}`,
    keywords: article.tags.join(', '),
    articleBody: article.content.replace(/<[^>]*>/g, '').substring(0, 500),
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 白洁传研究中心`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalPath={`/article/${article.id}`}
        ogType="article"
        schemaData={articleSchema}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-24 pb-16 px-4 md:px-6">
          <article className="max-w-4xl mx-auto">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  { label: article.categoryName, path: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />
            </div>

            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Link
                  to={`/category/${article.category}`}
                  className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium whitespace-nowrap hover:bg-primary-200 transition-colors"
                >
                  {article.categoryName}
                </Link>
                <span className="text-xs text-foreground-500 flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="text-xs text-foreground-500 flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.readTime}
                </span>
              </div>

              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight">
                {article.title}
              </h1>

              <p className="mt-4 text-foreground-600 text-base leading-relaxed">
                {article.excerpt}
              </p>

              <div className="mt-4 flex items-center gap-2 text-sm text-foreground-500">
                <span>作者：{article.author}</span>
              </div>
            </header>

            <div className="mb-10 rounded-lg overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-64 md:h-80 object-cover"
                title={`${article.title} - 白洁传研究中心`}
              />
            </div>

            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-6 border-t border-background-200/70">
              <h3 className="text-sm font-medium text-foreground-700 mb-3">标签</h3>
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-background-200/70 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className="text-sm text-foreground-600">分享到：</span>
                <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors" aria-label="分享到微信">
                  <i className="ri-wechat-line"></i>
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors" aria-label="分享到微博">
                  <i className="ri-weibo-line"></i>
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors" aria-label="复制链接">
                  <i className="ri-link"></i>
                </button>
              </div>
              <Link
                to={`/category/${article.category}`}
                className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 text-sm font-medium whitespace-nowrap transition-colors"
              >
                返回{article.categoryName}分类
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="mt-16 max-w-4xl mx-auto">
              <div className="border-t border-background-200/70 pt-10">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {relatedArticles.map((rel) => (
                    <ArticleCard key={rel.id} article={rel} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}